import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlContainer, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from 'app/core/material/material.module';
import { Observable, Subject, debounceTime, map, merge, shareReplay, startWith, switchMap } from 'rxjs';
import { UsersService } from 'app/services/users.service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { TeamUser } from 'app/models/user/user';
import { AutocompleteSelectMembersComponent } from 'app/shared/components/autocomplete-select-members/autocomplete-select-members.component';

@Component({
  selector: 'tm-members-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, NgxMatSelectSearchModule, AutocompleteSelectMembersComponent],
  templateUrl: './members-form.component.html',
  styleUrls: ['./members-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true })
    }
  ]
})
export class MembersFormComponent implements OnDestroy, OnInit, AfterViewInit {
  private readonly destroy$ = new Subject<void>();
  readonly usersFilterCtrl = new FormControl<string>('');
  @Output() private readonly init = new EventEmitter<void>();
  readonly parentContainer = inject(ControlContainer);
  readonly members$ = this.usersService.get().pipe(
    map(members => members.filter(member => !member.isI)),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  get projectMembersForm() {
    return this.parentContainer.control as FormGroup;
  }
  get membersFC() {
    return this.projectMembersForm?.get('members').get('members');
  }
  get managersFC() {
    return this.projectMembersForm?.get('members').get('managers');
  }
  managersList$: Observable<Array<TeamUser>>;
  membersList$: Observable<Array<TeamUser>>;

  constructor(private readonly usersService: UsersService) { }

  ngOnInit(): void {
    this.projectMembersForm.addControl('members', new FormGroup({}));
  }

  ngAfterViewInit(): void {
    this.init.emit();
    this.managersList$ = merge(this.membersFC?.valueChanges, this.managersFC?.valueChanges).pipe(
      startWith(this.membersFC?.value || []),
      debounceTime(150),
      switchMap(() => {
        const managersList = this.managersFC.value || [];
        const membersList = this.membersFC.value || [];
        return this.members$.pipe(
          map(members =>
            members.filter(
              (member) => (!managersList.map(user => user.id).includes(member.id)) && (!membersList.map(user => user.id).includes(member.id)))
          )
        )
      })
    );

    this.membersList$ = merge(this.membersFC?.valueChanges, this.managersFC?.valueChanges,).pipe(
      startWith(this.managersFC?.value || []),
      debounceTime(150),
      switchMap(() => {
        const teamLeadsList = this.managersFC.value || [];
        const membersList = this.membersFC.value || [];
        return this.members$.pipe(
          map(members =>
            members.filter(
              (member) => (!teamLeadsList.map(user => user.id).includes(member.id)) && (!membersList.map(user => user.id).includes(member.id)))
          )
        )
      })
    );
  }

  ngOnDestroy(): void {
    this.projectMembersForm.removeControl('members');
    this.destroy$.next();
    this.destroy$.complete();
  }
}